import { getCoreGroups } from '../objects/group_service.js?v=20260904c';
import * as SystemRegistry from '../registry/system_registry.js?v=20260904f';
import {
  getPrintPlanSelectedGroupKeys,
  isPrintPlanCaptureInProgress
} from './print_plan_context.js?v=20260826b';
import {
  applySceneGroupVisibility,
  normalizePrintPlanCoreGroups,
  normalizePrintPlanGroupKeys,
  printPlanGroupSelectionSignature,
  printPlanSystemObjectSelected,
  selectedPrintPlanCoreGroupBounds
} from './print_plan_group_filter_model.mjs?v=20260904c';

function normalizedSystemId(value) {
  const raw = String(value || '').trim();
  if (!raw) return '';
  return String(SystemRegistry.normalizeSystemId(raw) || raw).trim();
}

function currentPrintPlanGroupFilter(options = {}) {
  const selectedGroupKeys = options.selectedGroupKeys === undefined
    ? getPrintPlanSelectedGroupKeys()
    : options.selectedGroupKeys;
  const normalizedKeys = normalizePrintPlanGroupKeys(selectedGroupKeys);
  const groups = normalizePrintPlanCoreGroups(options.groups || getCoreGroups());
  return {
    groups,
    selectedGroupKeys: normalizedKeys,
    signature: printPlanGroupSelectionSignature(normalizedKeys)
  };
}

function shouldRenderPrintPlanSystemObject(systemId, objectId, options = {}) {
  if (!isPrintPlanCaptureInProgress()) return true;
  const filter = currentPrintPlanGroupFilter(options);
  return printPlanSystemObjectSelected(
    filter.groups,
    filter.selectedGroupKeys,
    systemId,
    objectId,
    { normalizeSystemId: normalizedSystemId }
  );
}

function selectedPrintPlanSystemObjectIds(systemId, options = {}) {
  if (!isPrintPlanCaptureInProgress()) return null;
  const filter = currentPrintPlanGroupFilter(options);
  if (filter.selectedGroupKeys === null) return null;
  const selected = new Set(filter.selectedGroupKeys);
  const targetSystemId = normalizedSystemId(systemId);
  const ids = new Set();
  filter.groups.forEach(group => {
    if (!selected.has(group.key) || normalizedSystemId(group.systemId) !== targetSystemId) return;
    group.objectIds.forEach(id => ids.add(id));
  });
  return ids;
}

function selectedPrintPlanGroupBounds(options = {}) {
  const filter = currentPrintPlanGroupFilter(options);
  return selectedPrintPlanCoreGroupBounds(filter.groups, filter.selectedGroupKeys);
}

// Applies the print selection only to group-owned nodes. Floors, ceilings,
// obstacles and other ungrouped scene content keep their existing state.
// Every touched visibility value is captured and can be restored exactly;
// this never writes S.view3d.groupVisibility or a project's saved settings.
function applyPrintPlanSceneGroupFilter(root, options = {}) {
  const filter = currentPrintPlanGroupFilter(options);
  const applied = applySceneGroupVisibility(root, filter.groups, filter.selectedGroupKeys, {
    normalizeSystemId: normalizedSystemId
  });
  return { ...filter, restore: applied.restore };
}

function elementAttributeEntries(element) {
  if (!element?.attributes) return [];
  return Array.from(element.attributes, attribute => [
    String(attribute?.name || '').toLowerCase(),
    String(attribute?.value || '').trim()
  ]);
}

function attributeTokens(value) {
  const text = String(value || '').trim();
  if (!text) return [];
  return [...new Set([text, ...text.split(/[\s,|;]+/)].map(token => token.trim()).filter(Boolean))];
}

function explicitSvgGroupKeys(element, validKeys) {
  const entries = elementAttributeEntries(element);
  const values = entries
    .filter(([name]) => name === 'data-core-group-key' || name === 'data-core-group-keys')
    .flatMap(([, value]) => attributeTokens(value));
  return {
    declared: values.length > 0,
    keys: [...new Set(values.filter(key => validKeys.has(key)))]
  };
}

function surroundingSvgSystemId(element) {
  const wrapper = element?.closest?.('.product-system-plan-layer[data-system-id]');
  return normalizedSystemId(wrapper?.getAttribute?.('data-system-id'));
}

function trustedSvgSystemId(element, trustedOwnerSystemId = '', trustedWrapper = null) {
  const suppliedOwner = normalizedSystemId(trustedOwnerSystemId);
  if (suppliedOwner && trustedWrapper) {
    if (typeof trustedWrapper.contains === 'function' && !trustedWrapper.contains(element)) return '';
    return suppliedOwner;
  }
  const wrapperOwner = surroundingSvgSystemId(element);
  if (suppliedOwner && wrapperOwner && suppliedOwner !== wrapperOwner) return '';
  return suppliedOwner || wrapperOwner;
}

function canonicalSvgGroupKey(group, ownerSystemId) {
  const groupOwner = normalizedSystemId(group?.systemId);
  const groupId = String(group?.groupId || '').trim();
  const key = String(group?.key || '').trim();
  if (!ownerSystemId || groupOwner !== ownerSystemId || !groupId) return '';
  return key === `${groupOwner}:${groupId}` ? key : '';
}

function svgElementGroupKeys(element, groups = [], trustedOwnerSystemId = '', trustedWrapper = null) {
  const systemId = trustedSvgSystemId(element, trustedOwnerSystemId, trustedWrapper);
  if (!systemId) return [];

  const ownerGroups = groups.filter(group => canonicalSvgGroupKey(group, systemId));
  const validKeys = new Set(ownerGroups.map(group => canonicalSvgGroupKey(group, systemId)));
  const explicit = explicitSvgGroupKeys(element, validKeys);
  if (explicit.declared) return explicit.keys;

  const entries = elementAttributeEntries(element);
  const ids = new Set(entries
    .filter(([name]) => (
      name !== 'data-system-id'
      && name.startsWith('data-')
      && /(?:^|-)ids?$/.test(name)
    ))
    .flatMap(([, value]) => attributeTokens(value)));
  if (!ids.size) return [];
  return ownerGroups.filter(group => {
    for (const id of ids) {
      if (group.objectIds.has(id)) return true;
    }
    return false;
  }).map(group => group.key);
}

function hasSvgSystemWrapperAncestor(wrapper) {
  let current = wrapper?.parentElement || wrapper?.parentNode || null;
  while (current) {
    if (current.matches?.('.product-system-plan-layer[data-system-id]')) return true;
    current = current.parentElement || current.parentNode || null;
  }
  return false;
}

function svgElementGroupMembership(element, groups, ownerSystemId, wrapper, validKeys) {
  const chain = [];
  let current = element || null;
  while (current && current !== wrapper) {
    chain.push(current);
    current = current.parentElement || current.parentNode || null;
  }

  // Explizite Marker definieren wie im 3D-Baum einen Subtree-Scope. Ein
  // ungültiger Marker darf deshalb nicht durch eine zufällig passende ID auf
  // einem Kind umgangen werden.
  for (const candidate of [...chain.slice(1), chain[0]].filter(Boolean)) {
    const explicit = explicitSvgGroupKeys(candidate, validKeys);
    if (explicit.declared) return { ...explicit, mapped: explicit.keys.length > 0 };
  }

  for (const candidate of chain) {
    const keys = svgElementGroupKeys(candidate, groups, ownerSystemId, wrapper);
    if (keys.length) return { declared: false, keys, mapped: true };
  }
  return { declared: false, keys: [], mapped: false };
}

// The live plan is cloned before this function runs, so removing nodes can
// never disturb the visible editor. Product systems already expose stable
// data-* IDs on their group/wall/item roots; ungrouped plan content remains.
function applyPrintPlanSvgGroupFilter(svgRoot, options = {}) {
  const filter = currentPrintPlanGroupFilter(options);
  if (!svgRoot?.querySelectorAll || filter.selectedGroupKeys === null) return filter;
  const selected = new Set(filter.selectedGroupKeys);
  const groupsBySystem = new Map();
  filter.groups.forEach(group => {
    const systemId = normalizedSystemId(group.systemId);
    if (!groupsBySystem.has(systemId)) groupsBySystem.set(systemId, []);
    groupsBySystem.get(systemId).push(group);
  });

  const wrappers = [...svgRoot.querySelectorAll('.product-system-plan-layer[data-system-id]')]
    .filter(wrapper => !hasSvgSystemWrapperAncestor(wrapper));
  for (const wrapper of wrappers) {
    const rawSystemId = wrapper.getAttribute('data-system-id') || '';
    const systemId = normalizedSystemId(rawSystemId);
    const systemGroups = groupsBySystem.get(systemId) || [];
    if (!systemGroups.length) continue;
    if (!systemGroups.some(group => selected.has(group.key))) {
      wrapper.remove();
      continue;
    }
    const allSystemGroupsSelected = systemGroups.every(group => selected.has(group.key));
    const validKeys = new Set(systemGroups
      .map(group => canonicalSvgGroupKey(group, systemId))
      .filter(Boolean));
    const elements = [...wrapper.querySelectorAll('*')];
    const memberships = new Map(elements.map(element => [
      element,
      svgElementGroupMembership(element, systemGroups, rawSystemId, wrapper, validKeys)
    ]));
    const selectedContainers = new Set();
    memberships.forEach((membership, element) => {
      if (!membership.keys.some(key => selected.has(key))) return;
      let current = element.parentElement || element.parentNode || null;
      while (current && current !== wrapper) {
        selectedContainers.add(current);
        current = current.parentElement || current.parentNode || null;
      }
    });

    for (const element of [...elements].reverse()) {
      const membership = memberships.get(element);
      const selectedMembership = membership.keys.some(key => selected.has(key));
      const invalidExplicit = membership.declared && !membership.mapped;
      const keepUnmappedContainer = !membership.mapped && selectedContainers.has(element);
      if (invalidExplicit
        || (membership.mapped && !selectedMembership)
        || (!allSystemGroupsSelected && !membership.mapped && !keepUnmappedContainer)) {
        element.remove();
      }
    }
  }
  return filter;
}

export {
  applyPrintPlanSceneGroupFilter,
  applyPrintPlanSvgGroupFilter,
  currentPrintPlanGroupFilter,
  selectedPrintPlanGroupBounds,
  selectedPrintPlanSystemObjectIds,
  shouldRenderPrintPlanSystemObject,
  svgElementGroupKeys
};
